/**
 * AADE Analytics Dashboard
 *
 * Comprehensive dashboard showing portfolio health, anomalies, and key metrics.
 */

import React, { useState, useEffect } from 'react';
import { PortfolioHealth, AnomalyAlert, SyncJob } from '../../services/aadeService';
import { aadeGatewayService } from '../../services/aadeGatewayService';
import { aadeUiUtils } from '../../services/aadeUiUtils';
import {
  Activity as FiActivity,
  AlertCircle as FiAlertCircle,
  CheckCircle2 as FiCheckCircle,
  RefreshCw as FiRefreshCw,
  TrendingUp as FiTrendingUp,
  Clock3 as FiClock,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AADEDashboard: React.FC = () => {
  const [portfolioHealth, setPortfolioHealth] = useState<PortfolioHealth | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalyAlert[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [healthData, anomalyData, syncData] = await Promise.all([
        aadeGatewayService.getPortfolioHealth(),
        aadeGatewayService.getAnomalyAlerts(24),
        aadeGatewayService.getSyncStatus(5),
      ]);

      setPortfolioHealth(healthData);
      setAnomalies(anomalyData.alerts);
      setSyncStatus(syncData.sync_jobs);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      await aadeGatewayService.triggerSync('full');
      alert('Sync triggered successfully. Refresh in a few moments to see results.');
      setTimeout(fetchDashboardData, 3000);
    } catch (err: any) {
      alert('Failed to trigger sync: ' + err.message);
    }
  };

  const handleAcknowledgeAnomaly = async (anomalyId: string) => {
    try {
      await aadeGatewayService.acknowledgeInsight(anomalyId);
      setAnomalies(anomalies.map((a) => (a.id === anomalyId ? { ...a, acknowledged: true } : a)));
    } catch (err: any) {
      alert('Failed to acknowledge anomaly: ' + err.message);
    }
  };

  const healthScore = portfolioHealth?.health_score || 0;
  const latestSync = syncStatus[0];

  const getHealthColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    if (score >= 0.4) return 'text-orange-600';
    return 'text-red-600';
  };

  const getHealthLabel = (score: number) => {
    if (score >= 0.8) return 'Excellent';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Fair';
    return 'Needs Attention';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AADE Analytics Dashboard</h1>
            <p className="text-gray-600 mt-2">AI-powered portfolio insights and monitoring</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleTriggerSync}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FiActivity />
              Trigger Sync
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Portfolio Health Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FiActivity className="text-blue-600" />
              Portfolio Health
            </h2>

            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className={`text-5xl font-bold ${getHealthColor(healthScore)}`}>
                      {(healthScore * 100).toFixed(0)}
                    </div>
                    <div className="text-gray-500 text-sm mt-1">Health Score</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{getHealthLabel(healthScore)}</span>
                      <span className="text-sm text-gray-500">{(healthScore * 100).toFixed(0)}%</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          healthScore >= 0.8
                            ? 'bg-green-600'
                            : healthScore >= 0.6
                            ? 'bg-yellow-600'
                            : healthScore >= 0.4
                            ? 'bg-orange-600'
                            : 'bg-red-600'
                        }`}
                        style={{ width: `${healthScore * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Insights */}
                {portfolioHealth?.insights && portfolioHealth.insights.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Key Insights</h3>
                    {portfolioHealth.insights.slice(0, 3).map((insight) => (
                      <div key={insight.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                        <FiTrendingUp className="text-blue-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{insight.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{insight.summary}</p>
                        </div>
                      </div>
                    ))}
                    <Link
                      to="/admin/aade/insights"
                      className="inline-block text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View all insights →
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sync Status Card */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FiClock className="text-gray-600" />
              Sync Status
            </h2>

            {latestSync ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {latestSync.status === 'completed' ? (
                    <FiCheckCircle className="text-green-600 text-xl" />
                  ) : latestSync.status === 'failed' ? (
                    <FiAlertCircle className="text-red-600 text-xl" />
                  ) : (
                    <FiRefreshCw className="text-blue-600 text-xl animate-spin" />
                  )}
                  <span
                    className={`font-medium ${aadeUiUtils.getSyncStatusColor(
                      latestSync.status
                    )}`}
                  >
                    {latestSync.status.charAt(0).toUpperCase() + latestSync.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{latestSync.sync_type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Records:</span>
                    <span className="font-medium">{latestSync.records_synced}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Started:</span>
                    <span className="font-medium">
                      {new Date(latestSync.started_at).toLocaleTimeString()}
                    </span>
                  </div>
                  {latestSync.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Completed:</span>
                      <span className="font-medium">
                        {new Date(latestSync.completed_at).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>

                <Link
                  to="/admin/aade/sync-status"
                  className="block mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View sync history →
                </Link>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No recent syncs</p>
            )}
          </div>
        </div>

        {/* Anomaly Alerts */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FiAlertCircle className="text-red-600" />
              Recent Anomalies
              {anomalies.filter((a) => !a.acknowledged).length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                  {anomalies.filter((a) => !a.acknowledged).length} new
                </span>
              )}
            </h2>
            <span className="text-sm text-gray-500">Last 24 hours</span>
          </div>

          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : anomalies.length === 0 ? (
            <div className="text-center py-8">
              <FiCheckCircle className="mx-auto text-5xl text-green-500 mb-3" />
              <p className="text-gray-600">No anomalies detected in the last 24 hours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {anomalies.slice(0, 5).map((anomaly) => (
                <div
                  key={anomaly.id}
                  className={`flex items-start justify-between p-4 rounded-lg border ${
                    anomaly.acknowledged
                      ? 'bg-gray-50 border-gray-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900">{anomaly.title}</h3>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          anomaly.priority === 'critical'
                            ? 'bg-red-100 text-red-800'
                            : anomaly.priority === 'high'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {anomaly.priority}
                      </span>
                      {anomaly.acknowledged && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Acknowledged
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{anomaly.summary}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Type: {anomaly.entity_type}</span>
                      <span>Generated: {new Date(anomaly.generated_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {!anomaly.acknowledged && (
                    <button
                      onClick={() => handleAcknowledgeAnomaly(anomaly.id)}
                      className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              ))}

              {anomalies.length > 5 && (
                <Link
                  to="/admin/aade/insights?type=anomaly_detection"
                  className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2"
                >
                  View all {anomalies.length} anomalies →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AADEDashboard;
